import type { MeasurementSource, WeightLogSource } from "./health";
import {
  QUESTION_SURFACES,
  type QuestionScaleOption,
  type QuestionSurface,
  type QuestionTimeframe,
} from "./question-register.v1";
import {
  isCurrentSymptomReport,
  SYMPTOM_QUESTIONS,
  symptomLabel,
  type SymptomEntryView,
  type SymptomSeverity,
  type SymptomUpdateView,
} from "./symptom-entry";

// TES-1255 (Ella 2.1 slice I) — "Din hälsa över tid": "Mätvärden", "Mående" and
// "Besvär" on one page. Drive "Ella 2.1" §3 (TES-1155):
//   "All data som vi presenterar i graf eller tidsserie ska hamna här varje gång patienten lämnar samma typ av input, oavsett om den kommer från en incheckning, dosregistrering, Besvärsguiden, Hem eller direktregistrering."
//   "Systemet ska tydligt skilja mellan: Sparat i min historik / Inskickat till Ella för granskning"
//
// So every point carries where she gave it (its origin) and where it went
// (its review route), and both are named here once, for the app and any
// later reader alike.

type Copy = { sv: string; en: string };

// ---------------------------------------------------------------------------
// Where a point went.

export const HEALTH_REVIEW_ROUTES = [
  "private_history",
  "submitted_for_review",
] as const;
export type HealthReviewRoute = (typeof HEALTH_REVIEW_ROUTES)[number];

/** Drive §3 verbatim (Swedish). The English is a working translation. */
export const HEALTH_REVIEW_ROUTE_LABELS: Record<HealthReviewRoute, Copy> = {
  private_history: { sv: "Sparat i min historik", en: "Saved in my history" },
  submitted_for_review: {
    sv: "Inskickat till Ella för granskning",
    en: "Sent to Ella for review",
  },
};

// ---------------------------------------------------------------------------
// Where a point came from.

/**
 * A register surface (contracts QUESTION_SURFACES), or one of the origins a
 * measurement can have that is not a surface she answers on: the care team
 * entered it, it was imported from an older store, or it came from "a
 * check-in" without saying which (the weight log's `checkin` source is
 * F-04 and F-05 alike).
 */
export type HealthOrigin =
  | QuestionSurface
  | "care_team"
  | "imported"
  | "checkin"
  // TES-1439 — the renewal view (CAR-18), a symptom surface that is not a
  // register surface, written by the renewal's chosen symptoms (TES-1443).
  | "renewal";

// The forms keep the readable names Emelie gave them (form-display-names.ts,
// TES-941). The other labels are working copy: the app's own names for the
// surfaces, pending an approved list (proposed as an open question on
// TES-1255).
export const HEALTH_ORIGIN_LABELS: Record<HealthOrigin, Copy> = {
  f01: { sv: "Inledande frågor", en: "Initial questions" },
  f02: { sv: "Medicinsk hälsodeklaration", en: "Medical health declaration" },
  f04: { sv: "Veckoincheckning", en: "Weekly check-in" },
  f05: { sv: "Månadsincheckning", en: "Monthly check-in" },
  f06: { sv: "Uppföljning", en: "Follow-up" },
  f07: {
    sv: "Läkemedel och behandlingsunderlag",
    en: "Medication and treatment documentation",
  },
  cp06: { sv: "Komplettering", en: "Completion" },
  completion: { sv: "Komplettering", en: "Completion" },
  dose: { sv: "Dosregistrering", en: "Dose registration" },
  besvarsguide: { sv: "Besvärsguiden", en: "Symptom guide" },
  health: { sv: "Din hälsa över tid", en: "Your health over time" },
  hem: { sv: "Hem", en: "Home" },
  care_team: { sv: "Vårdteamet", en: "Your care team" },
  imported: { sv: "Tidigare registrering", en: "Earlier registration" },
  checkin: { sv: "Incheckning", en: "Check-in" },
  renewal: { sv: "Förnyelse av recept", en: "Prescription renewal" },
};

// A check-in, a health declaration or a completion is read by the care team;
// a direct registration is hers only (Ö-447: "Direktregistrering … är inte en
// bevakad vårdkanal").
const REVIEWED_ORIGINS: ReadonlySet<HealthOrigin> = new Set<HealthOrigin>([
  "f01",
  "f02",
  "f04",
  "f05",
  "f06",
  "f07",
  "cp06",
  "completion",
  "checkin",
  "renewal",
]);
const PRIVATE_ORIGINS: ReadonlySet<HealthOrigin> = new Set<HealthOrigin>([
  "dose",
  "besvarsguide",
  "health",
  "hem",
]);

/**
 * The review route a measurement from this origin took. Null when it was
 * neither hers to save nor hers to send (the care team's entry, an import):
 * no label is better than a wrong one.
 */
export function healthReviewRouteFor(
  origin: HealthOrigin | null,
): HealthReviewRoute | null {
  if (!origin) return null;
  if (REVIEWED_ORIGINS.has(origin)) return "submitted_for_review";
  if (PRIVATE_ORIGINS.has(origin)) return "private_history";
  return null;
}

/**
 * A measurement occasion's origin. MeasurementSession.sourceSurface is free
 * text from before the register ("health/trends", "F-05",
 * "cp06/blood_pressure_pulse", "admin/patient/measurements", "weight-log",
 * and from TES-1249 "hem" and "F-04"), so it is read here rather than
 * trusted to be a register surface. Unknown text gives null, never a guess.
 */
export function measurementOrigin(
  source: MeasurementSource,
  sourceSurface: string | null | undefined,
): HealthOrigin | null {
  if (source === "clinician") return "care_team";
  if (source === "import") return "imported";
  const s = (sourceSurface ?? "").trim().toLowerCase();
  if (!s) return null;
  if (s.startsWith("health")) return "health";
  if (s === "hem") return "hem";
  if (s.startsWith("admin/")) return "care_team";
  if (s.startsWith("cp06")) return "cp06";
  // "weight-log": the original value of a weight log she corrected
  // (MeasurementService), recorded as a patient entry only when the log
  // was her own Home entry.
  if (s === "weight-log") return source === "patient" ? "hem" : null;
  const form = /^f-?0?(\d)$/.exec(s);
  if (form) {
    const surface = `f0${form[1]}`;
    return (QUESTION_SURFACES as readonly string[]).includes(surface)
      ? (surface as QuestionSurface)
      : null;
  }
  return (QUESTION_SURFACES as readonly string[]).includes(s)
    ? (s as QuestionSurface)
    : null;
}

/** A weight log row's origin (WeightLog has a source, no surface). */
export function weightLogOrigin(source: WeightLogSource): HealthOrigin {
  switch (source) {
    case "patient":
      return "hem";
    case "checkin":
      return "checkin";
    case "f01":
      return "f01";
    case "doctor":
    case "webdoc":
      return "care_team";
  }
}

// ---------------------------------------------------------------------------
// "Mående": the wellbeing series, by question id.

/**
 * The register questions "Mående" shows, in page order. Drive §3: "Mående –
 * övergripande mående, hunger, mättnad, sug/tankar på mat samt beslutade
 * energi- och funktionsmått." DRIVE_MOOD_CHANGE is the mood question both
 * check-ins ask (TES-1152 §7). The energy and function measures
 * (PROG_ENERGY, PROG_FUNCTION) are reserved ids with no copy yet; they join
 * this list when the register defines them.
 *
 * `chart`: whether the answers are on an ordered scale a line can join.
 *
 * `group`: the graph group the series is drawn in. TES-1478 (09B, Emelie
 * 2026-10-19): "Hunger, mättnad och sug/tankar på mat som en gemensam kompakt
 * yta … Samma område innebär inte att olika mätningar räknas ihop." The
 * three appetite questions share one area and one time axis, each in its own
 * chart with its own answers; nothing adds them up. The patient's and the
 * staff's views group by this list (wellbeingGroups), so both draw the same
 * areas.
 *
 * `neutral`: answers that are not a level of the series and are never
 * joined to its line, by how they are drawn:
 * - `varies` — TES-1450 (Ö-714): "Varierar/Varierande: grå diamant vid
 *   svarets datum, visuellt mitt i det aktuella deldiagrammet … Ordinarie
 *   linje ansluter inte till diamanten och bryts vid det svaret."
 * - `hard_to_say` — TES-1478 (09B): "Svårt att bedöma är ett annat svar än
 *   Varierar och frånvaro av svar … visas det som tydligt namngiven neutral
 *   markering vid datumet, utanför den graderade serien, utan numerisk poäng
 *   eller linjeanslutning."
 * - `improved` — TES-1654: F-05's "Ja, det har blivit bättre" on
 *   DRIVE_MOOD_CHANGE. The levels run from "Nej" (no change) to the most
 *   affected; an improvement is on neither side of them, so it is drawn as
 *   a marker of its own, never joined, as a "Varierar" is.
 * Every other answer is a level, in the order she sees it.
 *
 * TES-1478 — MONTH_HUNGER is charted by its named answers: "Hunger behåller
 * Mycket svag, Lagom/stabil, Mer än önskat som namngivna nivåer. Listordning
 * blir inte automatiskt en klinisk numerisk skala." Its answers still carry
 * no number; a level is a position on the chart only.
 * See answerChartPoints.
 *
 * `patient`: whether her own view shows the series. TES-1477 (09B,
 * Emelie 2026-10-19): "Driv/glädje/initiativ/lust stannar i klinisk
 * uppföljning och blir ingen egen patientgraf eller självregistrering i
 * denna version." DRIVE_MOOD_CHANGE is therefore the doctor's only; her
 * mood is the one WELLBEING_7D series, beside the appetite area.
 */
export const WELLBEING_SERIES_QUESTIONS = [
  {
    questionId: "WELLBEING_7D",
    chart: true,
    group: "wellbeing",
    neutral: {},
    patient: true,
  },
  {
    questionId: "DRIVE_MOOD_CHANGE",
    chart: true,
    group: "wellbeing",
    neutral: { battre: "improved" },
    patient: false,
  },
  {
    questionId: "MONTH_HUNGER",
    chart: true,
    group: "appetite",
    neutral: { varierande: "varies", svart_att_bedoma: "hard_to_say" },
    patient: true,
  },
  {
    questionId: "MONTH_SATIETY",
    chart: true,
    group: "appetite",
    neutral: { varierar: "varies" },
    patient: true,
  },
  {
    questionId: "MONTH_CRAVINGS",
    chart: true,
    group: "appetite",
    neutral: {},
    patient: true,
  },
] as const satisfies readonly {
  questionId: string;
  chart: boolean;
  group: WellbeingGroup;
  neutral: Readonly<Record<string, NeutralAnswer>>;
  patient: boolean;
}[];

/** The graph groups of "Mående", in page order (09B "Grafgrupper"). */
export const WELLBEING_GROUPS = ["wellbeing", "appetite"] as const;
export type WellbeingGroup = (typeof WELLBEING_GROUPS)[number];

/** How an answer outside a series' levels is drawn (see `neutral`). */
export type NeutralAnswer = "varies" | "hard_to_say" | "improved";

/**
 * Who reads the series: her own view, her doctor's (TES-1446), or her
 * coach's, which holds only what the register grants her (TES-1482,
 * coach-graphs.ts).
 */
export type WellbeingSeriesAudience = "patient" | "doctor" | "coach";
export type WellbeingSeriesQuestionId =
  (typeof WELLBEING_SERIES_QUESTIONS)[number]["questionId"];

export interface WellbeingPoint {
  /** The Observation it is read from. */
  id: string;
  /** ISO-8601. */
  observedAt: string;
  /** Her answer: an option value, or a 1–5 step as a string. */
  value: string;
  /** The answer as the scale words it. */
  label: Copy;
  /** Its position on the series; null when the scale gives none. */
  normalized: number | null;
  origin: HealthOrigin | null;
  reviewRoute: HealthReviewRoute | null;
}

export interface WellbeingSeries {
  questionId: WellbeingSeriesQuestionId;
  /** The question version every point was answered on. */
  registerVersion: number;
  /** The register's copy for the question. */
  question: Copy;
  timeframe: QuestionTimeframe;
  /** The answers, in scale order. */
  options: readonly Pick<
    QuestionScaleOption,
    "value" | "label" | "normalized"
  >[];
  chart: boolean;
  /** Oldest first. Empty when she has never answered it. */
  points: WellbeingPoint[];
}

/** One answer placed on an answer chart (answerChartPoints). */
export interface AnswerChartPoint {
  point: WellbeingPoint;
  /**
   * `line`: an answer at one of the series' levels, joined to the line's
   * other answers.
   * `marker`: a "Varierar"/"Varierande", an improvement (`improved`), or an
   * answer the series does not list, drawn on its own mid-chart and never
   * joined.
   * `aside`: a "Svårt att bedöma", drawn on its own outside the levels,
   * never joined and never placed where a "Varierar" is.
   */
  kind: "line" | "marker" | "aside";
  /**
   * Height from the top, 0–1: the answer's level, first level at the top;
   * 0.5 for a marker ("visuellt i mitten"); null for an aside, which has no
   * place among the levels. A drawing position only: no trend or rule may
   * read it.
   */
  y: number | null;
}

export interface AnswerChart {
  /** The series' levels, in the order she sees them, top first. */
  levels: { value: string; label: Copy }[];
  /** Oldest first, as the series. */
  points: AnswerChartPoint[];
}

/** The answers of a series that are not among its levels (see `neutral`). */
export function neutralAnswers(
  questionId: WellbeingSeriesQuestionId,
): Readonly<Record<string, NeutralAnswer>> {
  const q = WELLBEING_SERIES_QUESTIONS.find((x) => x.questionId === questionId);
  return q?.neutral ?? {};
}

/**
 * TES-1450 (Emelie 2026-10-19, Ö-714) — how a select series is drawn: its
 * levels in the order she sees them ("Övriga svar behåller egna etiketter
 * och sin ordning"), spaced as categories and not by any number, and a
 * "Varierar" as a marker in the middle. TES-1478 (09B) adds "Svårt att
 * bedöma" as an aside, outside the levels. Shared so every role's chart
 * draws the same ("Samma presentation gäller behöriga rollvyer").
 *
 * An answer is a level unless the series names it neutral. An answer the
 * series does not list at all is a marker, never joined, so a value off
 * the scale can never bend the line.
 */
export function answerChartPoints(series: WellbeingSeries): AnswerChart {
  const neutral: Readonly<Record<string, NeutralAnswer | undefined>> =
    neutralAnswers(series.questionId);
  const levels = series.options
    .filter((o) => neutral[o.value] === undefined)
    .map((o) => ({ value: o.value, label: o.label }));
  const step = levels.length > 1 ? 1 / (levels.length - 1) : 0;
  const points = series.points.map((point): AnswerChartPoint => {
    if (neutral[point.value] === "hard_to_say") {
      return { point, kind: "aside", y: null };
    }
    const i = levels.findIndex((l) => l.value === point.value);
    return i < 0
      ? { point, kind: "marker", y: 0.5 }
      : { point, kind: "line", y: levels.length > 1 ? i * step : 0.5 };
  });
  return { levels, points };
}

/** GET /patient-api/v1/health-over-time/wellbeing */
export interface WellbeingSeriesView {
  /**
   * Every question of WELLBEING_SERIES_QUESTIONS the audience reads, in that
   * order: the patient's view leaves out those with `patient: false`.
   */
  series: WellbeingSeries[];
}

/** One graph group of "Mående" and the series drawn in it. */
export interface WellbeingGroupView {
  group: WellbeingGroup;
  /** In WELLBEING_SERIES_QUESTIONS order; only the ones she has answered. */
  series: WellbeingSeries[];
  /**
   * The time axis every chart in the group shares, as epoch milliseconds:
   * her first and her latest answer in the group. 09B: "Gemensamt tidsval
   * gäller deldiagram inom samma grafgrupp."
   */
  from: number;
  to: number;
}

/**
 * TES-1478 — "Mående" as its graph groups, in page order: only the series
 * she has answered ("Visa endast serier med data"), and no group without
 * one. The patient's and the staff's views group by this, so both draw the
 * same areas on the same axes.
 */
export function wellbeingGroups(
  view: WellbeingSeriesView,
): WellbeingGroupView[] {
  const groupOf = new Map<string, WellbeingGroup>(
    WELLBEING_SERIES_QUESTIONS.map((q) => [q.questionId, q.group]),
  );
  return WELLBEING_GROUPS.flatMap((group): WellbeingGroupView[] => {
    const series = view.series.filter(
      (s) => groupOf.get(s.questionId) === group && s.points.length > 0,
    );
    if (series.length === 0) return [];
    const times = series.flatMap((s) =>
      s.points.map((p) => new Date(p.observedAt).getTime()),
    );
    return [
      { group, series, from: Math.min(...times), to: Math.max(...times) },
    ];
  });
}

// ---------------------------------------------------------------------------
// "Besvär": every entry with its history.

/** GET /patient-api/v1/symptoms/history */
export interface SymptomHistoryView {
  /**
   * Open entries first, then resolved, then history (TES-1439); by first
   * report, newest first, within each (the list's order). Every entry
   * carries `updates`, oldest first, including her "Borta" and, for a
   * client at symptomReferencePeriod, her before_ella reports: a severity
   * series reads only isCurrentSymptomReport. Older-format F-04/F-05
   * answers are never here (Ö-446): they are read from
   * /symptoms/older-registrations.
   */
  entries: (SymptomEntryView & {
    updates: NonNullable<SymptomEntryView["updates"]>;
  })[];
}

// ---------------------------------------------------------------------------
// TES-1480 (TES-1458 §5) — "Besvär" as one area with a series per symptom
// type. 09B, Emelie 2026-10-20:
// "Besvär: en gemensam yta med valbara serier för registrerade besvärstyper. Linjens namn anger besvärstyp; y-axeln anger intensitet enligt den fastställda jämförbara skalan, med exakta intensitetsetiketter."
// "Samma område innebär inte att olika mätningar räknas ihop."
//
// A series is one symptom code and nothing else: no total, no average, no
// point that mixes two types. Its points are her current reports
// (isCurrentSymptomReport) on the one question every current report
// answers, SYMPTOM_SEVERITY, so the types share a scale and may share a
// chart. A report about her earlier treatment (TES-1439), her "Borta" and a
// missing answer are never a point. Her older-format answers are not in
// SymptomHistoryView at all (Ö-446).
//
// Shared so every role's chart draws the same.

/** The severity answers in the order the axis shows them, lowest first. */
export const SYMPTOM_SEVERITY_LEVELS: readonly {
  value: SymptomSeverity;
  label: Copy;
}[] = SYMPTOM_QUESTIONS.SYMPTOM_SEVERITY.options.map((o) => ({
  value: o.value,
  label: o.label,
}));

export interface SymptomSeriesPoint {
  /** The SymptomUpdate it is read from. */
  id: string;
  entryId: string;
  /** ISO-8601. */
  observedAt: string;
  severity: SymptomSeverity;
  /** Index into SYMPTOM_SEVERITY_LEVELS. A drawing position only. */
  level: number;
  sourceSurface: SymptomUpdateView["sourceSurface"];
  reviewRoute: SymptomUpdateView["reviewRoute"];
  /**
   * Points that share a segment are joined. A new segment starts with each
   * entry and after her "Borta", so a line never runs across a time she
   * said the symptom was gone.
   */
  segment: number;
}

export interface SymptomSeries {
  symptomCode: string;
  label: Copy | null;
  /** Oldest first. Never empty. */
  points: SymptomSeriesPoint[];
}

type SeriesEntry = Pick<SymptomEntryView, "id" | "symptomCode" | "label"> & {
  updates: readonly Pick<
    SymptomUpdateView,
    | "id"
    | "kind"
    | "severity"
    | "observedAt"
    | "sourceSurface"
    | "reviewRoute"
    | "referencePeriod"
  >[];
};

/**
 * One series per symptom code with at least one current severity answer,
 * in the order the entries are listed (open first). Two entries of the same
 * code (one resolved, a later one opened beside it) are one type and so one
 * series, kept apart by their segments.
 */
export function symptomSeries(
  entries: readonly SeriesEntry[],
): SymptomSeries[] {
  const byCode = new Map<string, SymptomSeries>();
  let segment = 0;
  for (const entry of entries) {
    segment += 1;
    for (const u of entry.updates) {
      if (u.kind === "resolved") {
        segment += 1;
        continue;
      }
      if (!isCurrentSymptomReport(u) || u.severity === null) continue;
      const level = SYMPTOM_SEVERITY_LEVELS.findIndex(
        (l) => l.value === u.severity,
      );
      if (level < 0) continue;
      let series = byCode.get(entry.symptomCode);
      if (!series) {
        series = {
          symptomCode: entry.symptomCode,
          label: entry.label ?? symptomLabel(entry.symptomCode),
          points: [],
        };
        byCode.set(entry.symptomCode, series);
      }
      series.points.push({
        id: u.id,
        entryId: entry.id,
        observedAt: u.observedAt,
        severity: u.severity,
        level,
        sourceSurface: u.sourceSurface,
        reviewRoute: u.reviewRoute,
        segment,
      });
    }
  }
  const all = [...byCode.values()];
  for (const s of all) {
    s.points.sort(
      (a, b) =>
        new Date(a.observedAt).getTime() - new Date(b.observedAt).getTime(),
    );
  }
  return all;
}
